// node modules
import { computed, ComputedRef, Ref, ref } from 'vue'
import { AuthorizationParams, LogoutOptions, useAuth0 } from '@auth0/auth0-vue'
import { defineStore } from 'pinia'

// interfaces
import { UserImpersonationSubjectResponse } from '@skynamo/license_service_api'

// config
import { useConfigStore } from '@shared/stores/config'
import { CLIENT_ID_KEY, createAuthorizationParameters, INSTANCE_ID_KEY } from '@shared/helpers/AuthorizationParametersHelper'
import { RemoveFunctions } from '@shared/types/RemoveFunctions'
import { UnRef } from '@shared/types/UnRef'
import { useUserInfoStore } from '@shared/stores/user-info'

export declare interface AuthStore {
  authToken: Ref<string>;
  dc: Ref<string>;
  influxToken: Ref<string>;
  insightsUrl: Ref<string>;

  // user metadata
  username: Ref<string>;
  userId: Ref<string>;
  email: Ref<string>;
  authScope: Ref<AuthPermission[]>;
  radarActivated: ComputedRef<boolean>;
  isSkynamoAdmin: Ref<boolean>;
  impersonationSubject: Ref<UserImpersonationSubjectResponse | undefined>;

  // Instance metadata
  instanceName: Ref<string>;
  instanceId: Ref<string>;
  debugState: Ref<boolean>;

  // client metadata
  clientId: Ref<string>;
  isClientAdmin: Ref<boolean>;

  update: () => Promise<void>;
  logoutAndRedirect: () => Promise<void>;
}

export enum PermissionLevel {
  Create = 'create',
  Read = 'read',
  Update = 'update',
  Delete = 'delete',
  All = '*',
}

export const permissionLevelFromString = (level: string, environment: string): PermissionLevel | undefined => {
  switch (level) {
    case 'create':
      return PermissionLevel.Create
    case 'read':
      return PermissionLevel.Read
    case 'update':
      return PermissionLevel.Update
    case 'delete':
      return PermissionLevel.Delete
    case '*':
      return PermissionLevel.All
    default:
      if (environment !== 'production') {
        console.error(`Invalid permission level: ${level}. Expected one of "create", "read", "update", "delete", or "*".`)
      }
      return undefined
  }
}

export enum AuthScope {
  //Auth0
  OpenId = 'openid',
  Profile = 'profile',
  Email = 'email',

  //License Service
  Client = 'client',
  Instance = 'instance',
  Feature = 'feature',
  User = 'user',
  IntegratedOrganisation = 'integrated-organisation',
  Operational = 'operational',

  //Services
  Categories = 'categories',
  Catalogue = 'catalogue',
  Configuration = 'configuration',
  Customer = 'customer',
  Document = 'document',
  Order = 'order',
  Pricelist = 'pricing',
  Stock = 'stock',
  Contact = 'contact',
  Sync = 'sync',

  //Insights
  AnalyticsDashboard = 'dashboard',
  Timeline = 'timeline',
  Calendar = 'calendar',
  Products = 'products',
  Customers = 'customers',
  Contacts = 'contacts',
  Forms = 'forms',
  Users = 'users',
  Reports = 'reports',
  RfmResults = 'rfm-results',
  Settings = 'settings',
  Devices = 'devices',
  Logi = 'logi',
  Announcements = 'announcements',
  Preview = 'preview',
  TextToOrder = 'text-to-order',

  //Trade Portal
  TradePortal = 'trade-portal',
  TradeProduct = 'trade-product',
  UserTargets = 'user-targets',
}
const authScopeValues: Map<string, AuthScope> = new Map(Object.values(AuthScope).map(scope => [scope.toString(), scope]))

export const authScopeFromString = (scope: string, environment: string): AuthScope | undefined => {
  if (authScopeValues.has(scope)) {
    return scope as AuthScope
  } else {
    if (environment !== 'production') {
      console.error(`Invalid scope: ${scope}`)
    }
    return undefined
  }
}

export interface AuthPermission {
  level: PermissionLevel | undefined;
  scope: AuthScope;
}

export const authPermissionEquals = (this_: AuthPermission, other: AuthPermission): boolean => {
  return this_.level === other.level && this_.scope === other.scope
}

export const authPermissionFromString = (authScope: string, environment: string): AuthPermission | undefined => {
  const [scopeString, levelString] = authScope.split(':').toReversed()
  if (!scopeString) {
    if (environment !== 'production') {
      console.error(`Invalid authScope format. Expected format "level:scope" or auth0 scope. Received: ${authScope}`)
    }
    return undefined
  }
  const scope = authScopeFromString(scopeString, environment)
  if (scope === undefined) {
    return undefined
  }
  if (levelString === undefined) {
    return {
      level: undefined,
      scope: scope,
    }
  }
  const level = permissionLevelFromString(levelString, environment)
  return {
    level: level,
    scope: scope,
  }
}

export interface AuthMetadata {
  dc: string;
  client?: ClientMetadata;
  instance?: InstanceMetadata;
  user: UserMetadata;
  influx_token?: string;
}

interface ClientMetadata {
  id: string;
}

interface InstanceMetadata {
  id: string;
  name: string;
  debug: boolean;
}

interface UserMetadata {
  id: string;
  email?: string;
  full_name: string;
  is_skynamo_admin: boolean;
  impersonating_subject?: UserImpersonationSubjectResponse;
  scope?: string;
}

//When changing this make sure to change the equivalent function in Subscription Management UserPermissions.ts isLoggedInUserClientAdmin 
function isClientAdminScopes(scopes: AuthPermission[]) {
  const clientAdminAuthPermission = { level: PermissionLevel.All, scope: AuthScope.Client }
  return scopes.some(permission => authPermissionEquals(clientAdminAuthPermission, permission))
}

export const useAuthStore = defineStore('AuthStore', () => {
  const configStore = useConfigStore()
  const userInfoStore = useUserInfoStore()

  const authToken = ref<string>('')
  const insightsUrl = ref<string>('')

  // user meta data
  const username = ref<string>('')
  const userId = ref<string>('')
  const email = ref<string>('')
  const authScope = ref<AuthPermission[]>([])
  const isSkynamoAdmin = ref<boolean>(false)
  const influxToken = ref<string>('')
  const impersonationSubject = ref<UserImpersonationSubjectResponse | undefined>(undefined)

  // Instance meta data
  const dc = ref<string>('')
  const instanceName = ref<string>('')
  const instanceId = ref<string>('')
  const debugState = ref<boolean>(false)
  const clientId = ref<string>('')
  const isClientAdmin = ref<boolean>(false)

  // getters
  const radarActivated = computed<boolean>(
    () =>
      authScope.value.some(permission => authPermissionEquals({ level: PermissionLevel.Read, scope: AuthScope.RfmResults }, permission)) ||
      authScope.value.some(permission => authPermissionEquals({ level: PermissionLevel.All, scope: AuthScope.RfmResults }, permission))
  )

  const { logout, getAccessTokenSilently, checkSession, user, isAuthenticated, loginWithRedirect } = useAuth0()

  async function update(): Promise<void> {
    if (authToken.value.length > 0) {
      return
    }
    const authorizationParams: AuthorizationParams = createAuthorizationParameters({
      login_hint: email.value,
    })

    await checkSession({
      authorizationParams: {
        ...authorizationParams,
        source: 'AuthStore checkSession',
      },
    })

    const skynamoMetadata: AuthMetadata | undefined = user?.value?.['https://skynamo.com/meta']
    if (skynamoMetadata === undefined) {
      loginWithRedirect({
        authorizationParams: {
          ...authorizationParams,
          source: 'AuthStore no metadata loginWithRedirect',
        },
      })
      return
    }

    if (isAuthenticated) {
      await handleAuthenticatedUser(skynamoMetadata)
    }
  }

  async function handleAuthenticatedUser(skynamoMetadata: AuthMetadata): Promise<void> {
    try {
      const authorizationParams = createAuthorizationParameters({
        source: 'AuthStore handleAuthenticatedUser getAccessTokenSilently',
      })

      const accessTokenResponse = await getAccessTokenSilently({
        authorizationParams,
        detailedResponse: true,
      })

      updateAuthToken(accessTokenResponse)
      updateMetadata(skynamoMetadata)

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (window.Appcues) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.Appcues.identify(userId.value, {
          org: instanceName.value,
          instanceName: instanceName.value.toLowerCase(),
          instanceId: instanceId.value,
        })
      }

      if (configStore.env !== 'production' || debugState.value) {
        logDebugInfo()
      }
    } catch (error) {
      console.error('Error updating auth store:', error)
    }
  }

  function updateMetadata(skynamoMetadata: AuthMetadata) {
    dc.value = skynamoMetadata.dc
    influxToken.value = skynamoMetadata.influx_token ?? ''

    updateUserMetaData(skynamoMetadata.user)

    if (skynamoMetadata.client) {
      updateClientMetaData(skynamoMetadata.client)
    }
    if (skynamoMetadata.instance) {
      updateInstanceMetaData(skynamoMetadata.instance)
      updateInsightsUrl()
    }
  }

  function updateAuthToken(accessTokenResponse: { access_token: string, scope?: string }): void {
    authToken.value = accessTokenResponse.access_token
    authScope.value = accessTokenResponse.scope?.split(' ').map(scope => authPermissionFromString(scope, configStore.env)).filter(permission => permission !== undefined) ?? []
  }

  function updateUserMetaData(userMetaData: UserMetadata): void {
    email.value = userMetaData.email ?? ''
    username.value = userMetaData.full_name
    isSkynamoAdmin.value = userMetaData.is_skynamo_admin
    userId.value = userMetaData.id
    impersonationSubject.value = userMetaData?.impersonating_subject
  }

  function updateClientMetaData(clientMetadata: ClientMetadata) {
    clientId.value = clientMetadata.id
    //TODO: isClientAdmin is no longer passed in the metadata and is instead computed based on scopes received.
    //It might be worth removing this from metadata and only computing it based on the scopes
    isClientAdmin.value = isClientAdminScopes(authScope.value)

    localStorage.setItem(CLIENT_ID_KEY, clientMetadata.id)
  }

  function updateInstanceMetaData(instanceMetadata: InstanceMetadata): void {
    instanceName.value = instanceMetadata.name
    instanceId.value = instanceMetadata.id
    debugState.value = instanceMetadata.debug

    localStorage.setItem(INSTANCE_ID_KEY, instanceMetadata.id)
  }

  function updateInsightsUrl(): void {
    insightsUrl.value = instanceName.value && dc.value ? `https://${instanceName.value}.${dc.value}` : ''
  }

  function logDebugInfo(): void {
    console.info('AuthStore:', <RemoveFunctions<UnRef<AuthStore>>>{
      authToken: authToken.value,
      dc: dc.value,
      influxToken: influxToken.value,

      // User metadata
      email: email.value,
      userId: userId.value,
      username: username.value,
      impersonationSubject: impersonationSubject.value,
      isSkynamoAdmin: isSkynamoAdmin.value,
      authScope: authScope.value,

      // User scope metadata
      auth_scope: authScope.value,
      radarActivated: radarActivated.value,

      // Instance metadata 
      instanceName: instanceName.value,
      instanceId: instanceId.value,
      debugState: debugState.value,
      insightsUrl: insightsUrl.value,

      // Client metadata
      clientId: clientId.value,
      isClientAdmin: isClientAdmin.value,
    })
  }

  async function logoutAndRedirect() {
    if (configStore.env !== 'production') {
      console.info('logoutAndRedirect')
    }

    localStorage.clear()
    const isImpersonating = instanceName.value === 'skynamo' || !!impersonationSubject.value
    let isInInsights = false

    try {
      isInInsights = await isCurrentUserInInsights()
    } catch (err) {
      if (configStore.env !== 'production') {
        console.error('Error checking if current user is in Insights:', err)
      }
    }

    const shouldRedirectToInsights = insightsUrl.value && !isImpersonating && isInInsights

    const returnTo = shouldRedirectToInsights
      ? `${insightsUrl.value}/accounts/logout`
      : configStore.shellUrl
    const logoutOptions: LogoutOptions = { logoutParams: { returnTo } }

    if (configStore.env !== 'production') {
      console.info('Return to URL:', logoutOptions.logoutParams)
    }

    logout(logoutOptions)
  }

  async function isCurrentUserInInsights() {
    await userInfoStore.update()
    return userInfoStore.instances.some(instance => instance.instanceId === instanceId.value)
  }

  return <AuthStore>{
    authToken,
    dc,
    insightsUrl,
    influxToken,

    // user metadata
    username,
    userId,
    email,
    isSkynamoAdmin,
    impersonationSubject,

    // Instance metadata
    instanceName,
    instanceId,
    debugState,

    // Client metadata
    clientId,
    isClientAdmin,

    // user scope metadata
    authScope,
    radarActivated,

    // methods
    update,
    logoutAndRedirect,
  }
})